import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, Linkedin, Twitter, Instagram, ArrowUp } from 'lucide-react';
import { Button } from './ui/button';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFooterLinkClick = (path) => {
    // If it's an internal link (starts with /), navigate and scroll to top
    if (path.startsWith('/')) {
      // Small delay to allow navigation to complete before scrolling
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  const footerLinks = {
    solutions: [
      { name: 'Complete Restaurant System', path: '/solutions' },
      { name: 'Growth Accelerator', path: '/solutions' },
      { name: 'Multi-Location Package', path: '/solutions' },
      { name: 'Compare Plans', path: '/solutions' },
      { name: 'Pricing', path: '/pricing' }
    ],
    company: [
      { name: 'About PrismoSpark', path: '/about' },
      { name: 'Success Stories', path: '/success-stories' }
    ],
    support: [
      { name: 'FAQ', path: '/faq' },
      { name: 'Start Trial', path: '#trial' },
      { name: 'Contact', path: '/contact' }
    ],
    legal: [
      { name: 'Privacy Policy', path: '#privacy' },
      { name: 'Terms of Service', path: '#terms' },
      { name: 'Data Security', path: '#security' },
      { name: 'Compliance', path: '#compliance' }
    ]
  };

  return (
    <footer className="bg-gradient-to-br from-restaurant-dark via-restaurant-dark-800 to-restaurant-dark-900 text-white relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-gradient-to-br from-sienna-accent/15 to-sienna-accent-600/15 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-gradient-to-br from-sienna-accent/10 to-brand-gold/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Download Free Checklist Section */}
        <div className="mb-20 text-center">
          <div className="max-w-2xl mx-auto bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
            <h3 className="text-2xl lg:text-3xl font-bold mb-4">
              🚀 Grab Your Free Local SEO Checklist
            </h3>
            <p className="text-brand-ivory/80 mb-6">
              Get the restaurant SEO point checklist to start optimizing today.
            </p>
            <div className="flex flex-col gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-sienna-accent focus:border-transparent backdrop-blur-sm"
              />
              <Button 
                onClick={async () => {
                  try {
                    const response = await fetch('/point-checklist.pdf');
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'Point Checklist.pdf';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    } else {
                      // Fallback: generate a simple placeholder PDF-like file
                      const placeholder = new Blob([
                        'PrismoSpark - Point Checklist\n\nPlease replace /public/point-checklist.pdf with your PDF asset.'
                      ], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(placeholder);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = 'Point Checklist.pdf';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    }
                  } catch (e) {
                    const placeholder = new Blob([
                      'PrismoSpark - Point Checklist\n\nPlease replace /public/point-checklist.pdf with your PDF asset.'
                    ], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(placeholder);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = 'Point Checklist.pdf';
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    window.URL.revokeObjectURL(url);
                  }
                }}
                className="w-full bg-gradient-to-r from-sienna-accent to-sienna-accent-600 hover:from-sienna-accent-600 hover:to-sienna-accent-700 text-white px-6 py-3 font-semibold border-0 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                Download Checklist
              </Button>
            </div>
            <p className="text-brand-ivory/60 text-sm mt-3">
              No email submission. Instant local download.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-6 gap-12">
          {/* Company Info - Takes 2 columns */}
          <div className="lg:col-span-2 space-y-8">
            <Link to="/" className="flex items-center space-x-3 group">
              <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center transform group-hover:rotate-12 transition-transform duration-300 p-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_prismospark-resto/artifacts/759vyyk9_prismospark-2.png" 
                  alt="PrismoSpark Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <span className="text-2xl font-bold group-hover:bg-gradient-to-r group-hover:from-sienna-accent group-hover:to-sienna-accent-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300">
                PrismoSpark
              </span>
            </Link>
            
            <p className="text-brand-ivory/80 leading-relaxed">
              Helping restaurants dominate their local market through premium technology and proven growth systems. 
              Isabella AI + Premium Websites + Review Acceleration + Local SEO.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-brand-ivory/80 group hover:text-sienna-accent transition-colors duration-300">
                <MapPin size={18} className="text-sienna-accent" />
                <span>Serving restaurants across Canada, India & USA</span>
              </div>
              <div className="flex items-center space-x-3 text-brand-ivory/80 group hover:text-sienna-accent transition-colors duration-300">
                <Phone size={18} className="text-sienna-accent" />
                <span>1-800-PRISMOSPARK</span>
              </div>
              <div className="flex items-center space-x-3 text-brand-ivory/80 group hover:text-sienna-accent transition-colors duration-300">
                <Mail size={18} className="text-sienna-accent" />
                <span>hello@prismospark.com</span>
              </div>
            </div>

            {/* Enhanced Social Links */}
            <div className="flex space-x-4">
              {[
                { Icon: Linkedin, href: '#', color: 'hover:text-blue-400' },
                { Icon: Twitter, href: '#', color: 'hover:text-sky-400' },
                { Icon: Instagram, href: '#', color: 'hover:text-pink-400' },
                { Icon: Globe, href: '#', color: 'hover:text-green-400' }
              ].map(({ Icon, href, color }, index) => (
                <a 
                  key={index}
                  href={href} 
                  className={`w-10 h-10 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center text-brand-ivory/60 ${color} transition-all duration-300 hover:bg-white/10 hover:border-white/20 hover:scale-110`}
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </div>

          {/* Links Sections - Each takes 1 column */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h3 className="text-lg font-semibold mb-6 text-white">
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </h3>
              <ul className="space-y-4">
                {links.map((link, index) => (
                  <li key={index}>
                    <Link 
                      to={link.path}
                      onClick={() => handleFooterLinkClick(link.path)}
                      className="text-brand-ivory/80 hover:text-sienna-accent transition-all duration-300 hover:translate-x-1 inline-block"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Enhanced Bottom Section */}
        <div className="border-t border-white/10 mt-16 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-6 lg:space-y-0">
            <div className="flex flex-col space-y-4">
              {/* Copyright */}
              <p className="text-brand-ivory/60 text-center lg:text-left">
                © 2025 PrismoSpark. All rights reserved.
              </p>
              
              {/* Security & Compliance */}
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-1 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="text-green-400">🔒</span>
                  <span className="text-brand-ivory/80">256-bit SSL Encryption</span>
                  <span className="text-brand-ivory/40">•</span>
                  <span className="text-brand-ivory/80">SOC 2 Certified</span>
                  <span className="text-brand-ivory/40">•</span>
                  <span className="text-brand-ivory/80">GDPR Compliant</span>
                  <span className="text-brand-ivory/40">•</span>
                  <span className="text-brand-ivory/80">99.9% Uptime</span>
                </div>
              </div>

              {/* Global Serving */}
              <div className="flex items-center justify-center lg:justify-start space-x-2 text-sm">
                <span className="text-blue-400">🌍</span>
                <span className="text-brand-ivory/80">Serving restaurants in Canada, USA & India</span>
              </div>
            </div>
            
            {/* Back to Top Button */}
            <Button 
              onClick={scrollToTop}
              variant="outline"
              size="sm"
              className="border-white/20 bg-white/5 text-white hover:bg-white/10 hover:border-white/30 backdrop-blur-sm transition-all duration-300 hover:scale-105 flex-shrink-0"
            >
              <ArrowUp size={16} className="mr-2" />
              Back to Top
            </Button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;